import React, { useContext, useMemo } from 'react';
import { GetFeatureFlagsDocument } from 'src/graphql/common/queries/GetFeatureFlags.generated';
import { useTypedQuery } from 'src/graphql/hooks/useTypedApollo';
import { useNullableSessionUser } from './sessionContext';

interface Value {
    organizationId?: string;
    facilityId?: string;
    skip?: boolean;
}

interface Props {
    facilityId?: string;
    organizationId?: string;
    skip?: boolean;
    children?: React.ReactNode;
}

const context = React.createContext<Value | null>(null);

export const FeatureFlagsProvider = ({ facilityId, organizationId, skip, children }: Props) => {
    const parentContextValue = useContext(context);

    // useMemo required - context value passed to provider.
    // Without stable reference, all context consumers re-render on every parent render.
    const mergedValue = useMemo(
        () => ({
            organizationId: organizationId ?? parentContextValue?.organizationId,
            facilityId,
            skip: skip ?? parentContextValue?.skip,
        }),
        [
            parentContextValue?.organizationId,
            parentContextValue?.skip,
            facilityId,
            organizationId,
            skip,
        ],
    );

    return <context.Provider value={mergedValue}>{children}</context.Provider>;
};

interface FeatureFlagsParams {
    facilityId?: string | null;
    organizationId?: string | null;
    skip?: boolean;
}

export const useFeatureFlags = ({
    facilityId,
    organizationId,
    skip = false,
}: FeatureFlagsParams = {}) => {
    const me = useNullableSessionUser();
    const value = useContext(context);

    const { data, refetch, loading } = useTypedQuery(GetFeatureFlagsDocument, {
        variables: {
            facilityId: facilityId ?? value?.facilityId ?? undefined,
            organizationId: organizationId ?? value?.organizationId ?? undefined,
        },
        skip: !me || skip || value?.skip,
        fetchPolicy: 'cache-first',
    });

    const featureFlags = data?.featureFlags ?? [];

    return { featureFlags, refetch, loading };
};
